import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Edit2, Trash2, Mic2, Calendar, Mail, Image as ImageIcon, Eye, EyeOff, Sun } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PhotoModal } from "./PhotoModal";
import { FacebookImport } from "./FacebookImport";
import { SabbathSettings } from "./SabbathSettings";
import { GalleryPanel } from "./GalleryPanel";
import { ContentModal } from "./ContentModal";
import { Badge, formatDate } from "./adminUi";
import { EVENT_TYPE_LABELS } from "./galleryConstants";

const emptySermon = { title: "", speaker: "", date: "", topic: "Faith", description: "", youtube_url: "", audio_url: "", notes_url: "" };
const emptyEvent = { title: "", date: "", end_date: "", description: "", speaker: "", location: "", event_type: "one_time", recurrence_day: "Saturday", start_time: "", end_time: "", published: true };

const eventWhen = (ev) => {
  if (ev.event_type === "weekly") return `Every ${ev.recurrence_day}${ev.start_time ? ` · ${ev.start_time}` : ""}${ev.end_time ? ` – ${ev.end_time}` : ""}`;
  if (ev.event_type === "date_range") return `${formatDate(ev.date)} – ${formatDate(ev.end_date)}`;
  return formatDate(ev.date);
};

const primaryBtn = "inline-flex items-center gap-2 bg-[#003B5C] text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-[#002A42]";
const RowActions = ({ onEdit, onDelete, editId, deleteId }) => (
  <td className="p-4"><div className="flex justify-end gap-2">
    <button onClick={onEdit} data-testid={editId} className="p-2 text-[#003B5C] hover:bg-[#003B5C]/10 rounded-sm"><Edit2 size={14} /></button>
    <button onClick={onDelete} data-testid={deleteId} className="p-2 text-red-600 hover:bg-red-50 rounded-sm"><Trash2 size={14} /></button>
  </div></td>
);
const Table = ({ headers, children }) => (
  <div className="bg-white border border-gray-100 rounded-sm overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-[#F7F8FA] text-xs uppercase tracking-wider text-[#1E1E1E]/60"><tr>{headers}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sermons");
  const [sermons, setSermons] = useState([]);
  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [modal, setModal] = useState(null); // {type: 'sermon'|'event', data, id}
  const [photoModal, setPhotoModal] = useState(null); // null | {photo}
  const [fbOpen, setFbOpen] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const [s, e, m, p] = await Promise.all([api.get("/sermons"), api.get("/events/admin"), api.get("/contact"), api.get("/gallery/admin")]);
      setSermons(s.data); setEvents(e.data); setMessages(m.data); setPhotos(p.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onLogout = () => { logout(); navigate("/admin/login"); };
  const openSermonModal = (s = null) => setModal({ type: "sermon", data: s ? { ...s } : { ...emptySermon }, id: s?.id || null });
  const openEventModal = (e = null) => setModal({ type: "event", data: e ? { ...emptyEvent, ...e } : { ...emptyEvent }, id: e?.id || null });
  const failToast = (err, fallback) => { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : fallback); };

  const saveModal = async (e) => {
    e.preventDefault();
    const { id, created_at, recurrence, ...payload } = modal.data;
    const path = modal.type === "sermon" ? "/sermons" : "/events";
    try {
      if (modal.id) await api.put(`${path}/${modal.id}`, payload); else await api.post(path, payload);
      toast.success(`${modal.type === "sermon" ? "Sermon" : "Event"} ${modal.id ? "updated" : "added"}`);
      setModal(null); load();
    } catch (err) { failToast(err, "Save failed"); }
  };

  const removeItem = async (type, id, extraWarning = "") => {
    if (!window.confirm(`Delete this ${type}?${extraWarning ? `\n\n${extraWarning}` : ""}`)) return;
    try {
      await api.delete(type === "photo" ? `/gallery/${id}` : `/${type}s/${id}`);
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
  };

  const togglePhotoPublished = async (p) => {
    const { id, created_at, is_old, ...payload } = p;
    try {
      await api.put(`/gallery/${p.id}`, { ...payload, published: !p.published });
      toast.success(p.published ? "Photo hidden from public gallery" : "Photo is now visible");
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleEventPublished = async (ev) => {
    const { id, created_at, recurrence, ...payload } = ev;
    try { await api.put(`/events/${ev.id}`, { ...payload, published: !ev.published }); load(); }
    catch (err) { failToast(err, "Update failed"); }
  };

  const eventTitles = useMemo(() => events.map((e) => e.title), [events]);
  const photoLabels = useMemo(() => [...new Set(photos.map((p) => p.event_label).filter(Boolean))], [photos]);
  const filteredEvents = useMemo(() => events.filter((e) => eventTypeFilter === "all" || e.event_type === eventTypeFilter), [events, eventTypeFilter]);

  const tabs = [
    { key: "sermons", label: "Sermons", icon: Mic2, count: sermons.length },
    { key: "events", label: "Events", icon: Calendar, count: events.length },
    { key: "gallery", label: "Gallery", icon: ImageIcon, count: photos.length },
    { key: "messages", label: "Messages", icon: Mail, count: messages.length },
    { key: "settings", label: "Sabbath Banner", icon: Sun },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F7F8FA]">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-home-link" className="font-display font-bold text-[#003B5C] flex items-center gap-2">
              <span className="w-8 h-8 rounded-sm bg-[#003B5C] flex items-center justify-center text-white text-sm">E</span>
              <span>Admin</span>
            </Link>
            <span className="hidden sm:inline-block text-xs text-[#1E1E1E]/50">{user?.email}</span>
          </div>
          <button onClick={onLogout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 text-sm text-[#1E1E1E]/70 hover:text-[#003B5C]"><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
          {tabs.map(({ key, label, icon: Icon, count }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-all -mb-px ${tab === key ? "border-[#D4A437] text-[#003B5C]" : "border-transparent text-[#1E1E1E]/60 hover:text-[#003B5C]"}`}>
              <Icon size={16} /> {label} {count !== undefined && <span className="text-xs text-[#1E1E1E]/40">({count})</span>}
            </button>
          ))}
        </div>

        {tab === "sermons" && (
          <div data-testid="admin-sermons-panel">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-2xl font-bold text-[#003B5C]">Sermons</h2>
              <button onClick={() => openSermonModal()} data-testid="admin-new-sermon" className={primaryBtn}><Plus size={14} /> New Sermon</button>
            </div>
            <Table headers={<><th className="text-left p-4">Title</th><th className="text-left p-4 hidden md:table-cell">Speaker</th><th className="text-left p-4 hidden md:table-cell">Topic</th><th className="text-left p-4 hidden sm:table-cell">Date</th><th className="text-right p-4">Actions</th></>}>
              {sermons.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-[#1E1E1E]/50">No sermons yet.</td></tr>
              ) : sermons.map((s) => (
                <tr key={s.id} data-testid={`admin-sermon-row-${s.id}`} className="border-t border-gray-100">
                  <td className="p-4 font-medium text-[#003B5C]">{s.title}</td>
                  <td className="p-4 hidden md:table-cell text-[#1E1E1E]/70">{s.speaker}</td>
                  <td className="p-4 hidden md:table-cell"><span className="text-xs bg-[#D4A437]/15 text-[#D4A437] px-2 py-1 rounded-sm font-semibold uppercase tracking-wider">{s.topic}</span></td>
                  <td className="p-4 hidden sm:table-cell text-[#1E1E1E]/70">{formatDate(s.date)}</td>
                  <RowActions onEdit={() => openSermonModal(s)} onDelete={() => removeItem("sermon", s.id)} editId={`admin-edit-sermon-${s.id}`} deleteId={`admin-delete-sermon-${s.id}`} />
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "events" && (
          <div data-testid="admin-events-panel">
            <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
              <h2 className="font-display text-2xl font-bold text-[#003B5C]">Events</h2>
              <div className="flex items-center gap-3">
                <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)} data-testid="admin-event-type-filter" className="px-3 py-2.5 border border-gray-200 rounded-sm text-sm bg-white focus:outline-none focus:border-[#003B5C]">
                  <option value="all">All types</option>
                  {Object.entries(EVENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <button onClick={() => openEventModal()} data-testid="admin-new-event" className={primaryBtn}><Plus size={14} /> New Event</button>
              </div>
            </div>
            <Table headers={<><th className="text-left p-4">Title</th><th className="text-left p-4 hidden md:table-cell">Type</th><th className="text-left p-4">When</th><th className="text-left p-4 hidden sm:table-cell">Status</th><th className="text-right p-4">Actions</th></>}>
              {filteredEvents.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-[#1E1E1E]/50">No events{eventTypeFilter !== "all" ? " of this type" : " yet"}.</td></tr>
              ) : filteredEvents.map((ev) => (
                <tr key={ev.id} data-testid={`admin-event-row-${ev.id}`} className={`border-t border-gray-100 ${!ev.published ? "bg-[#F7F8FA]/60" : ""}`}>
                  <td className="p-4 font-medium text-[#003B5C]">
                    <div className="flex items-center gap-2 flex-wrap">{ev.title}{!ev.published && <Badge tone="gray" testId={`admin-event-draft-badge-${ev.id}`}>Draft</Badge>}</div>
                    {ev.speaker && <div className="text-xs text-[#1E1E1E]/50 mt-0.5">{ev.speaker}</div>}
                  </td>
                  <td className="p-4 hidden md:table-cell"><Badge tone={ev.event_type === "weekly" ? "gold" : "navy"} testId={`admin-event-type-badge-${ev.id}`}>{EVENT_TYPE_LABELS[ev.event_type] || "One-time"}</Badge></td>
                  <td className="p-4 text-[#1E1E1E]/70">{eventWhen(ev)}</td>
                  <td className="p-4 hidden sm:table-cell">
                    <button onClick={() => toggleEventPublished(ev)} data-testid={`admin-toggle-event-${ev.id}`} className="inline-flex items-center gap-1 px-2 py-1.5 border border-gray-200 text-xs text-[#003B5C] hover:border-[#003B5C] rounded-sm">
                      {ev.published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Hidden</>}
                    </button>
                  </td>
                  <RowActions onEdit={() => openEventModal(ev)} onDelete={() => removeItem("event", ev.id)} editId={`admin-edit-event-${ev.id}`} deleteId={`admin-delete-event-${ev.id}`} />
                </tr>
              ))}
            </Table>
          </div>
        )}

        {tab === "gallery" && (
          <GalleryPanel
            photos={photos}
            photoLabels={photoLabels}
            onNew={() => setPhotoModal({ photo: null })}
            onEdit={(p) => setPhotoModal({ photo: p })}
            onToggle={togglePhotoPublished}
            onDelete={(p) => removeItem("photo", p.id, p.is_old ? "This photo is older than 3 years. Consider hiding it instead — deleting is permanent." : "This is permanent.")}
            onFacebook={() => setFbOpen(true)}
          />
        )}

        {tab === "messages" && (
          <div data-testid="admin-messages-panel">
            <h2 className="font-display text-2xl font-bold text-[#003B5C] mb-6">Contact Messages</h2>
            {messages.length === 0 ? (
              <div className="bg-white border border-gray-100 rounded-sm p-12 text-center text-[#1E1E1E]/50">No messages yet.</div>
            ) : (
              <div className="space-y-4">
                {messages.map((m) => (
                  <article key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white border border-gray-100 p-6 rounded-sm">
                    <div className="flex flex-wrap justify-between gap-2 mb-3">
                      <div>
                        <h3 className="font-display text-lg font-bold text-[#003B5C]">{m.name}</h3>
                        <div className="text-xs text-[#1E1E1E]/60 mt-1 flex flex-wrap gap-3">
                          <a href={`mailto:${m.email}`} className="hover:text-[#003B5C]">{m.email}</a>
                          {m.phone && <a href={`tel:${m.phone}`} className="hover:text-[#003B5C]">{m.phone}</a>}
                        </div>
                      </div>
                      <span className="text-xs text-[#1E1E1E]/50">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-[#1E1E1E]/80 whitespace-pre-wrap">{m.message}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "settings" && <SabbathSettings />}
      </div>

      {photoModal && <PhotoModal photo={photoModal.photo} eventTitles={eventTitles} existingLabels={photoLabels} onClose={() => setPhotoModal(null)} onSaved={() => { setPhotoModal(null); load(); }} />}
      {fbOpen && <FacebookImport eventTitles={eventTitles} existingLabels={photoLabels} onClose={() => setFbOpen(false)} onImported={() => { setFbOpen(false); load(); }} />}
      {modal && <ContentModal modal={modal} setModal={setModal} onSubmit={saveModal} />}
    </div>
  );
}
